import { NextPage } from 'next'
import {
  specialitiesSearchSelectors,
  specialitiesSearchThunks,
} from '@ucheba/store/specialities/search'
import { EProgramTypes } from '@ucheba/utils/types/program'
import dynamic from 'next/dynamic'
import { getIDeviceComponent } from '@ucheba/utils/helpers/components'
import { IObjectDevicesComponents } from '@ucheba/utils/helpers/components/types'

import React, { useEffect, useMemo, useRef } from 'react'
import { ESpecialitiesDataKeys } from '@ucheba/store/specialities/search/types'
import { NextSeo } from 'next-seo'
import { NextPageContext } from 'next/dist/shared/lib/utils'
import { parseUrl } from '@ucheba/utils/helpers/router'
import { NextRouter, useRouter } from 'next/router'
import { isSubdomainMatches } from '@ucheba/utils/middlewares/defineLocationMiddleware'
import { Store } from 'redux'
import isEqual from '@ucheba/utils/helpers/core/isEqual'
import { useDispatch } from 'react-redux'
import { ISpecialitiesSearchPageProps } from './types'
import {
  getDescriptionWithExamSubjects,
  getFlatTags,
  getTitleWithExamSubjects,
} from './bll'
import {
  getCurrentTag,
  getDescriptionWithSection,
  getDescriptionWithTag,
  getIsInstitutionSection,
  getTitleWithSection,
  getTitleWithTag,
} from '../index/bll'
import Custom404 from '../../../404.page'
import { headerActions } from '../../../../components/Header/store'

const devicesPages: IObjectDevicesComponents = {
  desktop: dynamic(() => import('./desktop')),
  touch: dynamic(() => import('./touch')),
}

const SpecialitiesSearchPage: NextPage<ISpecialitiesSearchPageProps> = (props) => {
  const { content, canonical, notFound } = props

  const { query } = useRouter()

  const isRender = useRef(false)

  const lastQuery = useRef(query)

  const dispatch = useDispatch()

  const facetsQuery = useMemo(() => {
    const result = {} as Record<string, any>
    Object.keys(query).forEach((el) => {
      const facetName = el.replace(/[[]]/gm, '')
      result[facetName] = query[el]
    })
    return result
  }, [query])

  useEffect(() => {
    if (isRender.current && !isEqual(lastQuery.current, facetsQuery)) {
      dispatch(
        specialitiesSearchThunks.fetch({
          data: {
            ...facetsQuery,
            limit: 20,
            [ESpecialitiesDataKeys.topProgramTypes]: [
              Number(query[ESpecialitiesDataKeys.topProgramTypes]) ||
                EProgramTypes.higher,
            ],
          },
        })
      )
    } else {
      isRender.current = true
    }
    lastQuery.current = facetsQuery
  }, [dispatch, facetsQuery, query])

  if (notFound) {
    return <Custom404 />
  }

  return (
    <>
      <NextSeo
        canonical={canonical}
        title={content.seo.meta.title}
        description={content.seo.meta.description}
        openGraph={{}}
      />

      {getIDeviceComponent(props, devicesPages)}
    </>
  )
}

SpecialitiesSearchPage.getInitialProps = async (
  ctx: NextPageContext & { store: Store }
): Promise<any> => {
  const { store, pathname } = ctx
  const { dispatch, getState } = store
  const query = parseUrl(ctx.asPath || '').query as NextRouter['query']

  await dispatch(
    specialitiesSearchThunks.fetch({
      ctx,
      data: {
        ...query,
        [ESpecialitiesDataKeys.topProgramTypes]: [
          Number(query[ESpecialitiesDataKeys.topProgramTypes]) || EProgramTypes.higher,
        ],
      },
    })
  )

  const facets = specialitiesSearchSelectors.facets(store.getState())
  const tags = getFlatTags(facets)
  const currentTag = getCurrentTag(query)
  const isInstitutionsSection = getIsInstitutionSection(query, pathname)

  const isDomainWWW = isSubdomainMatches(ctx.req, store, 'www')

  const canonical = isDomainWWW
    ? undefined
    : 'https://www.ucheba.ru/for-abiturients/speciality/search'

  dispatch(headerActions.toggleHeader(true))

  const canIndex = specialitiesSearchSelectors.canIndex(getState())
  const seo = specialitiesSearchSelectors.seo(getState())

  return {
    canonical,
    content: {
      seo: {
        meta: {
          title:
            seo?.h1 ||
            getTitleWithExamSubjects({
              query,
              facets,
            }) ||
            getTitleWithTag({
              tags,
              currentTag,
              isInstitutionsSection,
            }) ||
            getTitleWithSection({ isInstitutionsSection }),
          description:
            getDescriptionWithExamSubjects({
              query,
              facets,
            }) ||
            getDescriptionWithTag({
              tags,
              currentTag,
              isInstitutionsSection,
            }) ||
            getDescriptionWithSection({ isInstitutionsSection }),
        },
      },
      canIndex,
    },
  }
}

export default SpecialitiesSearchPage
